import { logD, logE } from "@blacknut/logging/dist";
import axios from "axios";
import apiService from "../../api/ApiService";
import { ClientDeviceType, UserAgent } from "../../model";
const TAG = "Analytics";

export class AnalyticsService {
  private _endpoint: string;
  public get endpoint(): string {
    return this._endpoint;
  }
  public set endpoint(v: string) {
    this._endpoint = v;
  }

  private _enabled = true;
  public get enabled(): boolean {
    return this._enabled;
  }
  public set enabled(v: boolean) {
    this._enabled = v;
  }

  private _userAgent?: UserAgent;
  public get userAgent(): UserAgent | undefined {
    return this._userAgent;
  }
  public set userAgent(v: UserAgent | undefined) {
    this._userAgent = v;
    this.fillDefaultValuesFromUserAgent(v);
  }

  private fillDefaultValuesFromUserAgent(v: UserAgent | undefined) {
    this.setDeviceOS(v?.os);
    this.setDeviceType(v?.client);
  }

  constructor() {
    this._endpoint = "https://a.blacknut.com/event";
    this._userAgent = apiService.userAgent;
  }

  private defaultValues: Record<string, string> = {};

  public setDeviceOS(os?: string) {
    this.setDefaultParam("device_os", os);
  }

  private setDefaultParam(key: string, value?: string) {
    if (value) {
      this.defaultValues[key] = value.toLocaleLowerCase();
    } else {
      delete this.defaultValues[key];
    }
  }

  public setDeviceType(type?: ClientDeviceType) {
    this.setDefaultParam("device_type", type);
  }

  public setConnectionType(type?: string) {
    this.setDefaultParam("conn_type", type);
  }

  public setUserId(id: string) {
    this.setDefaultParam("user_uuid", id);
  }

  public setDeviceId(id?: string) {
    this.setDefaultParam("device_id", id);
  }

  public setApp(app: string) {
    this.setDefaultParam("app", app);
  }

  public setDeviceBrowser(browser: string) {
    this.setDefaultParam("device_browser", browser);
  }
  public setDeviceScreen(screen: string) {
    this.setDefaultParam("device_screen", screen);
  }
  public setDeviceTheme(theme: string) {
    this.setDefaultParam("device_theme", theme);
  }
  public setTzCountry(country: string) {
    this.setDefaultParam("tz_country", country);
  }
  public setTzRegion(region: string) {
    this.setDefaultParam("tz_region", region);
  }

  public trackEvent(name: string, params?: Record<string, string | number | boolean>) {
    if (!this.endpoint) {
      logE(TAG, "No endpoint specified");
      return;
    }
    if (!this.enabled) {
      logD(TAG, "Disabled");
      return;
    }
    // By default reuse default values from apiService ua
    if (!this._userAgent) {
      this.fillDefaultValuesFromUserAgent(apiService.userAgent);
    }
    const newParams: Record<string, string | number | boolean> = {
      ...this.defaultValues,
      ...params,
    };

    const orderedParams = {
      event: name,
      ...Object.keys(newParams)
        .sort()
        .reduce((obj: Record<string, string | number | boolean>, key: string) => {
          obj[key] = newParams[key];
          return obj;
        }, {}),
    };

    const request = axios.request<void>({
      url: this.endpoint,
      method: "GET",
      params: orderedParams,
    });
    request
      .then(() => {
        logD(TAG, "Analytics OK");
      })
      .catch((e) => {
        logE(TAG, "Analytics KO", e);
      });
  }
}
